import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';

import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import {
  attachClosestEdge,
  extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
import { preserveOffsetOnSource } from '@atlaskit/pragmatic-drag-and-drop/element/preserve-offset-on-source';

import { faGripVertical } from '@fortawesome/pro-solid-svg-icons';
import { Icon } from 'js/components';
import DropIndicator from 'js/components/Assessment/Editors/common/components/DropIndicator';
import SentimentMarker from 'js/components/Icons/SentimentMarker';

import styles from './Sentiment.module.scss';

function SentimentOptionPreview({
  choice,
  icon,
  rotate,
  shrink,
  color,
  ink,
  colorOnly,
}) {
  return (
    <div className={classNames(styles.option, styles['option-preview'])}>
      <span className="drag-handle">
        <Icon icon={faGripVertical} className="icon" />
      </span>
      <div
        className={styles.decoration}
        style={{ '--response-color': color, '--response-ink': ink }}
      >
        <SentimentMarker
          icon={icon}
          rotate={rotate}
          shrink={shrink}
          colorOnly={colorOnly}
        />
      </div>
      <div
        className={classNames(
          styles['option-text'],
          'line-clamp-var',
          !choice.text && styles['option-text-empty']
        )}
        style={{ '--line-clamp': 3 }}
      >
        {choice.text || 'Describe this option'}
      </div>
    </div>
  );
}

/**
 * One sentiment option, draggable top-to-bottom within its question.
 *
 * Likert choices in the survey editor drop left/right because they sit in a
 * wrapping row. These options are a vertical list, so the drop edges match
 * questions: top and bottom. `canDrop` also requires the same `questionId`,
 * which is what keeps an option from landing on a different question.
 */
export default function DraggableSentimentOption({
  choice,
  index,
  questionId,
  answerSetId,
  icon,
  rotate,
  shrink,
  color,
  ink,
  colorOnly = false,
  onMove = () => {},
  children,
}) {
  const ref = useRef(null);
  const dragHandleRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [isDraggedOver, setIsDraggedOver] = useState(false);
  const [closestEdge, setClosestEdge] = useState(null);

  const choiceId = choice.guid ?? choice.id;

  useEffect(() => {
    const el = ref.current;
    const dragHandle = dragHandleRef.current;

    if (!el) {
      throw new Error('No element found');
    }
    if (!dragHandle) {
      throw new Error('No drag handle found');
    }

    return combine(
      draggable({
        element: el,
        dragHandle,
        getInitialData: () => ({
          sentimentOption: true,
          choice,
          index,
          answerSetId,
          questionId,
        }),
        onDragStart: () => setDragging(true),
        onDrop: () => setDragging(false),
        onGenerateDragPreview: ({ nativeSetDragImage, source, location }) => {
          const width = source.element.getBoundingClientRect().width;
          setCustomNativeDragPreview({
            nativeSetDragImage,
            getOffset: preserveOffsetOnSource({
              element: source.element,
              input: location.current.input,
            }),
            render({ container }) {
              // Paint this preview in isolation so overlapping neighbors
              // from the live list cannot leak into the drag image.
              container.style.width = `${width}px`;
              container.style.overflow = 'hidden';
              container.style.backgroundColor = '#fff';
              container.style.isolation = 'isolate';
              ReactDOM.render(
                <SentimentOptionPreview
                  choice={choice}
                  icon={icon}
                  rotate={rotate}
                  shrink={shrink}
                  color={color}
                  ink={ink}
                  colorOnly={colorOnly}
                />,
                container
              );
              return () => {
                ReactDOM.unmountComponentAtNode(container);
              };
            },
          });
        },
      }),
      dropTargetForElements({
        element: el,
        canDrop: ({ source }) =>
          source.data.sentimentOption &&
          source.data.questionId === questionId &&
          source.data.answerSetId === answerSetId &&
          (source.data.choice.guid ?? source.data.choice.id) !== choiceId,
        getData: (args) =>
          attachClosestEdge(
            { choice, index, questionId },
            {
              input: args.input,
              element: args.element,
              allowedEdges: ['top', 'bottom'],
            }
          ),
        onDragEnter: () => setIsDraggedOver(true),
        onDragLeave: () => {
          setClosestEdge(null);
          setIsDraggedOver(false);
        },
        onDrag({ self, source }) {
          if (source.element === el) {
            setClosestEdge(null);
            return;
          }

          const edge = extractClosestEdge(self.data);
          const sourceIndex = source.data.index;
          const isHidden =
            (index === sourceIndex - 1 && edge === 'bottom') ||
            (index === sourceIndex + 1 && edge === 'top');

          setClosestEdge(isHidden ? null : edge);
        },
        onDrop: ({ source, location }) => {
          setIsDraggedOver(false);
          setClosestEdge(null);

          const dropTarget = location.current.dropTargets[0];
          if (!dropTarget) {
            return;
          }

          const edge = extractClosestEdge(dropTarget.data);
          if (!edge) {
            return;
          }

          onMove(
            source.data.choice.guid ?? source.data.choice.id,
            choiceId,
            edge === 'bottom'
          );
        },
        getIsSticky: (args) => {
          const underCursor = document.elementFromPoint(
            args.input.pageX,
            args.input.pageY
          );
          return (
            underCursor &&
            underCursor.closest(`[data-sentiment-options="${questionId}"]`)
          );
        },
      })
    );
  }, [
    answerSetId,
    choice,
    choiceId,
    color,
    colorOnly,
    icon,
    index,
    ink,
    onMove,
    questionId,
    rotate,
    shrink,
  ]);

  return (
    <div ref={ref} className={classNames(styles.option, { dragging })}>
      {isDraggedOver && closestEdge === 'top' && <DropIndicator top />}
      <button
        type="button"
        className="drag-handle"
        ref={dragHandleRef}
        title="Reorder this option"
      >
        <Icon icon={faGripVertical} className="icon" />
      </button>
      {children}
      {isDraggedOver && closestEdge === 'bottom' && <DropIndicator bottom />}
    </div>
  );
}
